import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { ExternalLink } from "lucide-react";
import { FormDialog } from "../../ui/form-dialog";
import { Skeleton } from "../../ui/skeleton";
import { Checkbox } from "../ui/checkbox";
import {
  useAcceptLegalDocuments,
  useLegalDocumentVersion,
  useOnboardingStatus,
} from "../../../hooks/legal/useLegalDocuments";
import { useToast } from "../../../contexts/toast/Context";
import { useAuth } from "../../../contexts/auth/Context";
import { PUBLIC_PRIVACY_POLICY_URL } from "../../../constants/legal";
import {
  LEGAL_DOCUMENT_LABELS,
  type PendingLegalDocument,
} from "../../../utils/zod/legal";
import { cn } from "@/lib/utils";

const noop = () => {};

const PublishedVersionLink = () => (
  <a
    href={PUBLIC_PRIVACY_POLICY_URL}
    target="_blank"
    rel="noreferrer"
    className="inline-flex items-center gap-1.5 text-xs text-global-accent hover:underline"
  >
    Ver versión publicada en dashcore.cl
    <ExternalLink className="h-3 w-3" />
  </a>
);

const DocumentPanel = ({ versionPublicId }: { versionPublicId: string }) => {
  const { data, isLoading, isError } = useLegalDocumentVersion(versionPublicId);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="text-sm text-global-secondary">
        No se pudo cargar el documento. Vuelve a intentarlo o consulta{" "}
        <a
          href={PUBLIC_PRIVACY_POLICY_URL}
          target="_blank"
          rel="noreferrer"
          className="text-global-accent underline"
        >
          la versión publicada
        </a>
        .
      </p>
    );
  }

  return (
    <div className="legal-markdown">
      <ReactMarkdown>{data.content}</ReactMarkdown>
    </div>
  );
};

const PendingDocumentsForm = ({
  pending,
}: {
  pending: PendingLegalDocument[];
}) => {
  const [activeId, setActiveId] = useState(pending[0]!.versionPublicId);
  const [accepted, setAccepted] = useState<Record<string, boolean>>({});
  const { mutate, isPending } = useAcceptLegalDocuments();
  const { showToast } = useToast();
  const { logout } = useAuth();

  const activeDocument =
    pending.find((doc) => doc.versionPublicId === activeId) ?? pending[0]!;
  const allAccepted = pending.every((doc) => accepted[doc.versionPublicId]);
  const isUpdate = pending.some((doc) => doc.version > 1);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!allAccepted) {
      showToast("Debes aceptar todos los documentos para continuar", "error");
      return;
    }
    mutate(pending.map((doc) => doc.versionPublicId));
  };

  return (
    <FormDialog
      open
      onOpenChange={noop}
      onSubmit={handleSubmit}
      loading={isPending}
      title={
        pending.length === 1
          ? LEGAL_DOCUMENT_LABELS[pending[0]!.type]
          : "Términos y Política de Privacidad"
      }
      subtitle={
        isUpdate
          ? "Actualizamos estos documentos. Léelos y acéptalos para seguir usando Dashcore."
          : "Para continuar usando Dashcore, lee y acepta estos documentos."
      }
      showCloseButton={false}
      cancelLabel="Cerrar sesión"
      onCancel={logout}
      submitLabel="Aceptar y continuar"
      submitLoadingText="Guardando..."
      submitDisabled={!allAccepted}
      maxWidth="lg"
    >
      <div className="space-y-4">
        {pending.length > 1 && (
          <div className="flex gap-1 border-b border-global-primary">
            {pending.map((doc) => (
              <button
                key={doc.versionPublicId}
                type="button"
                onClick={() => setActiveId(doc.versionPublicId)}
                className={cn(
                  "px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px",
                  activeId === doc.versionPublicId
                    ? "border-(--text-accent) text-global-accent"
                    : "border-transparent text-global-secondary hover:text-global-primary",
                )}
              >
                {LEGAL_DOCUMENT_LABELS[doc.type]}
              </button>
            ))}
          </div>
        )}

        <div className="h-72 overflow-y-auto rounded border border-global-primary bg-global-overlay p-4">
          <DocumentPanel versionPublicId={activeDocument.versionPublicId} />
        </div>

        <PublishedVersionLink />

        <div className="space-y-2 pt-2 border-t border-global-primary">
          {pending.map((doc) => (
            <Checkbox
              key={doc.versionPublicId}
              checked={Boolean(accepted[doc.versionPublicId])}
              onChange={(value) =>
                setAccepted((prev) => ({
                  ...prev,
                  [doc.versionPublicId]: value,
                }))
              }
              label={`He leído y acepto ${LEGAL_DOCUMENT_LABELS[doc.type]} (versión ${doc.version}).`}
            />
          ))}
        </div>
      </div>
    </FormDialog>
  );
};

const StatusDialog = ({
  subtitle,
  onRetry,
  isRetrying,
}: {
  subtitle: string;
  onRetry?: () => void;
  isRetrying?: boolean;
}) => {
  const { logout } = useAuth();

  return (
    <FormDialog
      open
      onOpenChange={noop}
      onSubmit={(e) => {
        e.preventDefault();
        onRetry?.();
      }}
      loading={Boolean(isRetrying)}
      title="Términos y Política de Privacidad"
      subtitle={subtitle}
      showCloseButton={false}
      cancelLabel="Cerrar sesión"
      onCancel={logout}
      submitLabel={onRetry ? "Reintentar" : "Aceptar y continuar"}
      submitLoadingText="Reintentando..."
      submitDisabled={!onRetry}
      maxWidth="lg"
    >
      <div className="space-y-3">
        {onRetry ? <PublishedVersionLink /> : null}
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    </FormDialog>
  );
};

export const AcceptLegalDialog = () => {
  const { data, isError, isFetching, refetch } = useOnboardingStatus();

  if (isError) {
    return (
      <StatusDialog
        subtitle="No pudimos cargar los documentos pendientes."
        onRetry={() => void refetch()}
        isRetrying={isFetching}
      />
    );
  }

  // Sin datos todavía, o el servidor dice que no falta nada: el gate se
  // libera apenas `OnboardingGate` sincroniza la respuesta al contexto.
  if (!data || data.pendingLegalDocuments.length === 0) {
    return <StatusDialog subtitle="Cargando los documentos pendientes..." />;
  }

  return (
    <PendingDocumentsForm
      key={data.pendingLegalDocuments.map((d) => d.versionPublicId).join("|")}
      pending={data.pendingLegalDocuments}
    />
  );
};
